import { showAddOrEditFineState } from "@/atoms";
import CustomInput from "@/components/common/inputs/CustomInput";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import { useGetAllCouncils } from "@/services/councils";
import { useGetServicesByCouncil } from "@/services/services";
import { useCreateFine, useUpdateFine } from "@/services/fines";
import { Button, NumberInput, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { ModalLayout } from "moh-ui";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export default function AddUpdateFine() {
    const [show, setShow] = useRecoilState(showAddOrEditFineState);
    const { user } = useAuth();
  const council = useUserCouncil();
    const [data, setData] = useState({
        baseRatio: 0,
        safePeriodDays: 0,
        increaseUntilDay: 0,
        councilId: null as string | null,
        serviceId: null as string | null,
        productCode: '',
        description: '',
    });

    const {
        mutate: createFine,
        isPending: creatingFine
    } = useCreateFine();

    const {
        mutate: updateFine,
        isPending: updatingFine
    } = useUpdateFine();

    const queryClient = useQueryClient();

    const {
        data: services,
    } = useGetServicesByCouncil({
        councilId: data.councilId || undefined,
        enabled: !!data.councilId,
    });

    const {
        data: councils,
    } = useGetAllCouncils();

    useEffect(() => {
        if (show?.action === 'edit' && show.fine) {
            setData({
                baseRatio: show.fine.baseRatio,
                safePeriodDays: show.fine.safePeriodDays,
                increaseUntilDay: show.fine.increaseUntilDay,
                councilId: show.fine.council.id,
                serviceId: show.fine.service.id,
                productCode: show.fine.productCode || '',
                description: show.fine.description || '',
            });
        } else {
            setData({
                baseRatio: 0,
                safePeriodDays: 0,
                increaseUntilDay: 0,
                councilId: user?.roles?.[0]?.roleName === "ADMINCOUNCIL" ? (council?.id ?? null) : null,
                serviceId: null,
                productCode: '',
                description: '',
            });
        }
    }, [show, user]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!data.councilId) {
            notifications.show({
                title: 'Error',
                message: 'Please select a council',
                color: 'red'
            });
            return;
        }

        if (!data.serviceId) {
            notifications.show({
                title: 'Error',
                message: 'Please select a service',
                color: 'red'
            });
            return;
        }

        // Convert percentage to decimal (0.0-1.0) as backend expects
        const baseRatioDecimal = data.baseRatio;

        const params = {
            councilId: data.councilId,
            serviceId: data.serviceId,
            baseRatio: baseRatioDecimal,
            safePeriodDays: data.safePeriodDays,
            increaseUntilDay: data.increaseUntilDay,
            productCode: data.productCode || undefined,
            description: data.description || undefined,
        };

        if (show?.action === 'add') {
            createFine(params, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Fine Added Successfully",
                        color: "green",
                    });
                    // Invalidate all fines queries to refresh the data
                    queryClient.invalidateQueries({
                        queryKey: ["fines"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["get-fines"],
                    });
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to add fine",
                        color: "red",
                    });
                }
            });
        } else if (show?.action === 'edit' && show.fine) {
            updateFine({
                id: show.fine.id,
                ...params,
            }, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Fine Updated Successfully",
                        color: "green",
                    });
                    // Invalidate all fines queries to refresh the data
                    queryClient.invalidateQueries({
                        queryKey: ["fines"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["get-fines"],
                    });
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to update fine",
                        color: "red",
                    });
                }
            });
        }
    };

    return (
        <ModalLayout
            open={Boolean(show?.show)}
            onClose={() => setShow(null)}
        >
            <div className="mb-5">
                <h2 className="text-lg font-semibold">
                    {show?.action === 'add' ? 'Add Fine' : 'Update Fine'}
                </h2>
                <p className="text-sm text-gray-500">
                    {show?.action === 'add' ? 'Fill in the details to create a new fine.' : 'Update the fine details as needed.'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user?.roles?.[0]?.roleName === "ADMIN" && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Select Council</label>
                            <Select
                                value={data.councilId ?? ""}
                                onChange={(val) => setData({ ...data, councilId: val })}
                                data={councils?.data.map((item) => ({
                                    value: item.id,
                                    label: item.councilName + " (" + item.councilCode + ")",
                                })) || []}
                                placeholder="Select Council"
                                required
                                className="w-full"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1">Select Service</label>
                        <Select
                            value={data.serviceId ?? ""}
                            onChange={(val) => setData({ ...data, serviceId: val })}
                            data={services?.data?.map((item) => ({
                                value: item.id,
                                label: item.name,
                            })) || []}
                            placeholder="Select Service"
                            disabled={!data.councilId}
                            required
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Product Code</label>
                        <input
                            type="text"
                            value={data.productCode}
                            onChange={(e) => setData({ ...data, productCode: e.target.value.toUpperCase() })}
                            placeholder="e.g., LATE_SUBMISSION"
                            className="w-full p-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <input
                            type="text"
                            value={data.description}
                            onChange={(e) => setData({ ...data, description: e.target.value })}
                            placeholder="Fine description"
                            className="w-full p-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Base Ratio (%)</label>
                        <NumberInput
                            value={data.baseRatio * 100}
                            onChange={(val) => setData({ ...data, baseRatio: (Number(val) || 0) / 100 })}
                            min={0}
                            max={100}
                            required
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Safe Period (Days)</label>
                        <NumberInput
                            value={data.safePeriodDays}
                            onChange={(val) => setData({ ...data, safePeriodDays: Number(val) || 0 })}
                            min={0}
                            required
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Increase Until (Days)</label>
                        <NumberInput
                            value={data.increaseUntilDay}
                            onChange={(val) => setData({ ...data, increaseUntilDay: Number(val) || 0 })}
                            min={0}
                            required
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="flex justify-end items-center gap-4 pt-4 border-t">
                    <Button
                        variant="outline"
                        color="gray"
                        onClick={() => setShow(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={creatingFine || updatingFine}
                    >
                        {show?.action === 'add' ? 'Create Fine' : 'Update Fine'}
                    </Button>
                </div>
            </form>
        </ModalLayout>
    );
}
